import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { Icon } from '../Icon';
import { Glyph } from './glyphs';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';
import { SkipLink, MAIN_ID } from './SkipLink';
import { ThemeToggle } from './ThemeToggle';
import { routes } from '../../../routes/routes';
import './shells.css';
import { Wordmark } from '../../brand';

/** The default proof points. Three to five words each, never sentences. */
export const AUTH_PROOFS = [
  'Every listing priced against the market',
  'Sellers verified before they trade',
  /* NOT "Nationwide shipping on every order", and this is the one proof point
     that may not come back without a mechanism behind it.
     `SystemSetting.sell_in_person` can put the whole app in in-person-only
     mode, and while it is on the only fulfilment checkout offers is meeting
     the seller — so the login splash was promising nationwide delivery to
     someone about to discover there is none. Unlike the cart and the buy box,
     THIS surface cannot check: `GET /api/dashboard/system-setting` is
     IsAuthenticated and nobody reading this page has signed in yet.
     Escrow is the claim that survives, because it is a mechanism the app
     actually has, on every order, in both modes. */
  'Money held safely until you have your cards',
];

/**
 * AuthShell. No app chrome at all.
 *
 * Login, signup, forgot password, OTP, reset password, admin login.
 *
 * Desktop is a split: a 46 percent brand plate on the left and a 400px form
 * column centred in the 54 percent that is left. Mobile is the same form
 * column, on its own, centred.
 *
 * WHAT BRANCHES ON `useViewport()` AND WHY
 * ----------------------------------------
 * Two things, both deliberate and both small.
 *
 * 1. THE BRAND PLATE. It is a headline, a wordmark and a list of proof points
 *    with icons, and below 1024 it has no place to go. Hiding it with CSS would
 *    ship every word of it to a phone to be read by a screen reader and then
 *    painted over, so it is simply not mounted.
 *
 * 2. THE CLASS PREFIX. Below 1024 the frame is `ds-*`, at and above it is
 *    `dsk-*`. The shell must not hand a page a `dsk-` container to put `ds-`
 *    fields inside. The two prefixes share one rule block in shells.css
 *    wherever the styling is identical, so this costs a class name, not a
 *    duplicate stylesheet.
 *
 * Everything else here is one DOM tree and a media query's worth of difference.
 *
 * @param {{
 *   children: React.ReactNode,
 *   title?: string,
 *   subtitle?: React.ReactNode,
 *   headline?: string,
 *   proofs?: Array<string>,
 *   back?: { to?: string, onClick?: () => void, label?: string },
 *   footer?: React.ReactNode,
 *   showThemeToggle?: boolean,
 *   className?: string
 * }} props
 */
export function AuthShell({
  children,
  title,
  subtitle,
  headline = 'The South African home for trading card collectors.',
  proofs = AUTH_PROOFS,
  back,
  footer,
  showThemeToggle = true,
  className,
}) {
  const { isDesktop } = useViewport();
  const prefix = isDesktop ? 'dsk' : 'ds';
  const track = isDesktop ? 'desktop' : 'mobile';

  const backLabel = back?.label || 'Back to marketplace';
  const backNode = back ? (
    back.to ? (
      <Link to={back.to} className={`${prefix}-authshell__back`}>
        {isDesktop ? <Icon as={ArrowLeft} size="sm" /> : <Glyph name="arrowLeft" />}
        {backLabel}
      </Link>
    ) : (
      <button type="button" className={`${prefix}-authshell__back`} onClick={back.onClick}>
        {isDesktop ? <Icon as={ArrowLeft} size="sm" /> : <Glyph name="arrowLeft" />}
        {backLabel}
      </button>
    )
  ) : null;

  return (
    <div className={cn(`${prefix}-authshell`, className)}>
      <SkipLink track={track} />

      {isDesktop && (
        <aside className="dsk-authshell__brand">
          <Wordmark className="dsk-authshell__wordmark" size="lg" />
          <div className="dsk-authshell__brandbody">
            <p className="dsk-authshell__valueline">{headline}</p>
            <ul className="dsk-authshell__proofs">
              {proofs.map((proof) => (
                <li className="dsk-authshell__proof" key={proof}>
                  <Icon as={CheckCircle} size="lg" />
                  {proof}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      )}

      <div className={`${prefix}-authshell__col`}>
        {backNode}
        {!isDesktop && (
          <Wordmark className="ds-authshell__mobilebrand" />
        )}

        <main id={MAIN_ID} className={`${prefix}-authshell__form`}>
          {title && <h1 className={`${prefix}-authshell__title`}>{title}</h1>}
          {subtitle && <p className={`${prefix}-authshell__sub`}>{subtitle}</p>}
          {children}
          {footer && <div className={`${prefix}-authshell__foot`}>{footer}</div>}
        </main>
      </div>

      {showThemeToggle && <ThemeToggle track={track} variant="floating" />}
    </div>
  );
}

export default AuthShell;
